import { useMemo } from 'react';

import {
  SkuAssemblyComponentDTO,
  SkuComponentGroupDTO,
  SkuDTO,
  SkuType,
} from '@invenco/products-interface';

import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';

type SavePayload = {
  newMysteryComponent: MysteryComponent;
  existingMysteryComponent?: MysteryComponent;
};

/**
 * The primary purpose of this hook is to manage and translate between the groups and assembly components in a mystery SKU
 * and "mystery components". A mystery component represents a single part of the mystery SKU which will be fulfilled.
 *
 * This can either be:
 *   - A SkuAssemblyComponent with no group (also called a fixed SKU component), which is added directly to the order.
 *     This will be translated to a MysterySkuComponent, which is basically just a SkuAssemblyComponentDTO.
 *   - A SkuComponentGroup comprised of one or more SKU components (options); one or more of these options will be added
 *     to the order depending on the group's selection method and availability of the components. This will be
 *     translated to a MysteryGroupComponent, which is a SkuComponentGroupDTO with its components joined to it.
 */

export type MysterySkuComponent = Partial<Omit<SkuAssemblyComponentDTO, 'id'>> & { id: string };
export type MysteryGroupComponent = Pick<
  SkuComponentGroupDTO,
  'id' | 'name' | 'quantity' | 'selectionMethods'
> & {
  components: MysterySkuComponent[];
};

export type MysteryComponent = MysterySkuComponent | MysteryGroupComponent;

export const isMysteryGroupComponent = (
  mysteryComponent: MysteryComponent,
): mysteryComponent is MysteryGroupComponent => 'components' in mysteryComponent;

export const getMysterySkuComponents = (
  mysteryComponent?: MysteryComponent,
): MysterySkuComponent[] => {
  if (!mysteryComponent) return [];
  return isMysteryGroupComponent(mysteryComponent)
    ? mysteryComponent.components
    : [mysteryComponent];
};

type UseMysteryComponentsResult = {
  mysteryComponents: MysteryComponent[];
  saveMysteryComponent: (payload: SavePayload) => Promise<Result>;
  deleteMysteryComponent: (index: number) => Promise<Result>;
};

export function useMysteryComponents(sku: Partial<SkuDTO>): UseMysteryComponentsResult {
  const { productsGateway } = useGateways();

  const mysteryComponents = useMemo(() => {
    const finalComponents: MysteryComponent[] = [];
    if (sku?.type !== SkuType.MYSTERY) return finalComponents;
    const componentsByGroupId = new Map<string, SkuAssemblyComponentDTO[]>();

    sku.components?.forEach((component) => {
      if (component.componentGroupId) {
        if (!componentsByGroupId.has(component.componentGroupId)) {
          componentsByGroupId.set(component.componentGroupId, []);
        }
        componentsByGroupId.get(component.componentGroupId)!.push(component);
      } else {
        finalComponents.push(component);
      }
    });

    sku.componentGroups?.forEach((group) => {
      finalComponents.push({
        id: group.id,
        name: group.name,
        quantity: group.quantity,
        selectionMethods: group.selectionMethods,
        components: componentsByGroupId.get(group.id) ?? [],
      });
    });

    return finalComponents;
  }, [sku]);

  const { mutate: saveMysteryComponent } = useGatewayMutation({
    mutationFn: async ({ newMysteryComponent, existingMysteryComponent }: SavePayload) => {
      if (!sku?.id) throw new Error('No SKU available');
      const wasGrouped =
        existingMysteryComponent && isMysteryGroupComponent(existingMysteryComponent);
      const isGrouped = isMysteryGroupComponent(newMysteryComponent);
      let componentGroupId = isGrouped ? newMysteryComponent.id : null;

      if (!wasGrouped && isGrouped) {
        if (!newMysteryComponent.name) {
          throw new Error('Missing name for new component group');
        }
        if (!newMysteryComponent.quantity) {
          throw new Error('Missing quantity for new component group');
        }
        const componentGroup = await productsGateway.createSkuComponentGroup(sku.id, {
          name: newMysteryComponent.name,
          quantity: newMysteryComponent.quantity,
          selectionMethods: newMysteryComponent.selectionMethods,
        });
        componentGroupId = componentGroup.id;
      } else if (wasGrouped && existingMysteryComponent) {
        if (isGrouped) {
          await productsGateway.updateSkuComponentGroup(sku.id, existingMysteryComponent.id, {
            name: newMysteryComponent.name,
            quantity: newMysteryComponent.quantity,
            selectionMethods: newMysteryComponent.selectionMethods,
          });
        } else {
          await productsGateway.deleteSkuComponentGroup(sku.id, existingMysteryComponent.id);
        }
      }

      const preexistingSkuIds = new Set(
        getMysterySkuComponents(existingMysteryComponent).map((comp) => comp.componentSkuId),
      );
      const components = [
        ...(sku.components?.filter((comp) => !preexistingSkuIds.has(comp.componentSkuId)) ?? []),
        ...getMysterySkuComponents(newMysteryComponent).map((comp) => ({
          componentSkuId: comp.componentSkuId,
          quantity: comp.quantity,
          componentGroupId,
        })),
      ];

      return productsGateway.updateAssemblyComponents(
        sku.id,
        components.map((comp) => ({
          componentSkuId: comp.componentSkuId,
          quantity: comp.quantity,
          componentGroupId: comp.componentGroupId,
        })),
      );
    },
    linkedQuery: ['skus', sku.id],
  });

  const { mutate: deleteMysteryComponent } = useGatewayMutation({
    mutationFn: async (index: number) => {
      if (!sku?.id) throw new Error('No SKU available');
      const component = mysteryComponents[index];
      if (!component) return;
      if (isMysteryGroupComponent(component)) {
        await productsGateway.deleteSkuComponentGroup(sku.id, component.id);
      } else {
        await productsGateway.updateAssemblyComponents(
          sku.id,
          sku.components
            ?.filter((comp) => comp.id !== component.id)
            .map((comp) => ({
              componentSkuId: comp.componentSkuId,
              quantity: comp.quantity,
              componentGroupId: comp.componentGroupId,
            })) ?? [],
        );
      }
    },
    linkedQuery: ['skus', sku.id],
    linkResponseToQuery: false,
    successMessage: 'Deleted component',
  });

  return { mysteryComponents, deleteMysteryComponent, saveMysteryComponent };
}
